import React, { useState, useMemo, useEffect } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import PageTitle from '@/components/Admin/PageTitle';
import FileUploader from '@/components/Admin/UI/FileUploader';
import PartnerShareLink from '@/components/Admin/PartnerShareLink';
import { useResellerStatus } from '@/hooks/useResellerStatus';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import {
   listInquiries as apiListInquiries,
   updateMyResellerProfileApi,
   listResellerInboxApi,
   approveResellerInquiryApi,
   fulfillResellerInquiryApi,
   rejectResellerInquiryApi,
   type ApiInquiry,
   type ApiReseller,
} from '@/api/vendo';
import SoldCreditsTable from '@/components/Admin/SoldCreditsTable';

type Tab = 'requests' | 'transfers' | 'purchases' | 'profile';
const VALID_TABS: Tab[] = ['requests', 'transfers', 'purchases', 'profile'];
const TAB_STORAGE_KEY = 'smartfi-reseller-tab';

const loadTab = (): Tab => {
   try {
      const v = localStorage.getItem(TAB_STORAGE_KEY);
      if (v && (VALID_TABS as string[]).includes(v)) return v as Tab;
   } catch {}
   return 'purchases';
};

// On buyer-scope inquiries the server populates `distributor` with a small
// profile object; fall back to the raw id string if not populated.
type PopulatedDistributor = {
   _id: string;
   displayName?: string;
   businessName?: string;
   city?: string;
   region?: string;
};
const distributorName = (d: ApiInquiry['distributor']): string => {
   if (d && typeof d === 'object') {
      const p = d as unknown as PopulatedDistributor;
      return p.displayName || p.businessName || 'Distributor';
   }
   return 'Distributor';
};

const PURCHASE_STATUS_LABEL: Record<string, string> = {
   pending: 'Pending',
   approved: 'Approved — pay & await credit',
   fulfilled: 'Credited',
   rejected: 'Rejected',
};

const Reseller = () => {
   const navigate = useNavigate();
   const { isReseller, isPending, reseller, isLoading: statusLoading } =
      useResellerStatus();
   const [tab, setTab] = useState<Tab>(loadTab);

   useEffect(() => {
      try {
         localStorage.setItem(TAB_STORAGE_KEY, tab);
      } catch {}
   }, [tab]);

   if (statusLoading) {
      return (
         <Wrapper>
            <PageTitle title="Reseller" actions={[]} />
            <SkeletonLines lines={3} />
         </Wrapper>
      );
   }

   // Not an approved reseller — explain and route them to apply.
   if (!isReseller) {
      return (
         <Wrapper>
            <PageTitle title="Reseller" actions={[]} />
            <Banner>
               <BannerIcon>🤝</BannerIcon>
               <BannerBody>
                  <BannerTitle>
                     {isPending
                        ? 'Your reseller application is pending'
                        : "You're not a reseller yet"}
                  </BannerTitle>
                  <BannerText>
                     {isPending
                        ? 'Your distributor still has to approve your application. Once approved, your transactions show up here.'
                        : 'Apply under a distributor on the Partners page. Once they approve you, your reseller dashboard appears here.'}
                  </BannerText>
                  {!isPending && (
                     <BannerBtn onClick={() => navigate('/wifi-partners')}>
                        Go to Partners
                     </BannerBtn>
                  )}
               </BannerBody>
            </Banner>
         </Wrapper>
      );
   }

   return (
      <Wrapper>
         <PageTitle title="Reseller" actions={[]} />

         <TabBar>
            <TabBtn $active={tab === 'requests'} onClick={() => setTab('requests')}>
               Requests
            </TabBtn>
            {/* Repurposed from "Credits Transfers": wallet transfers never
                contain sales, so this was always empty for a reseller. Sales
                rows appear once reseller-as-seller ships (①c) — until then
                the empty state explains itself. */}
            <TabBtn $active={tab === 'transfers'} onClick={() => setTab('transfers')}>
               Sold Credits
            </TabBtn>
            <TabBtn $active={tab === 'purchases'} onClick={() => setTab('purchases')}>
               Credits Purchases
            </TabBtn>
            <TabBtn $active={tab === 'profile'} onClick={() => setTab('profile')}>
               Profile
            </TabBtn>
         </TabBar>

         {tab === 'requests' && <ResellerInboxPane />}
         {tab === 'transfers' && <SoldCreditsTable />}
         {tab === 'purchases' && <PurchasesPane />}
         {tab === 'profile' && <ProfilePane reseller={reseller} />}
      </Wrapper>
   );
};

/* ─── Credits Purchases (from the reseller's distributor) ───────────── */

const PurchasesPane: React.FC = () => {
   const { data: inquiries = [], isLoading } = useQuery({
      queryKey: ['vendo', 'inquiries', 'buyer'],
      queryFn: () => apiListInquiries({ scope: 'buyer' }),
   });

   const totalBought = useMemo(
      () =>
         inquiries
            .filter((i) => i.status === 'fulfilled')
            .reduce((s, i) => s + i.requestedCredits, 0),
      [inquiries]
   );
   const totalSpent = useMemo(
      () =>
         inquiries
            .filter((i) => i.status === 'fulfilled')
            .reduce(
               (s, i) =>
                  s +
                  i.requestedCredits *
                     (i.negotiatedPricePerCredit ?? i.proposedPricePerCredit),
               0
            ),
      [inquiries]
   );

   if (isLoading) return <SkeletonLines lines={3} />;

   return (
      <>
         <StatsRow>
            <StatCard>
               <StatLbl>Credits bought</StatLbl>
               <StatVal $green>{totalBought.toLocaleString()}</StatVal>
               <StatSub>fulfilled, all-time</StatSub>
            </StatCard>
            <StatCard>
               <StatLbl>Total spent</StatLbl>
               <StatVal>₱{totalSpent.toLocaleString()}</StatVal>
               <StatSub>on credits from your distributor</StatSub>
            </StatCard>
            <StatCard>
               <StatLbl>Requests</StatLbl>
               <StatVal>{inquiries.length.toLocaleString()}</StatVal>
               <StatSub>all-time</StatSub>
            </StatCard>
         </StatsRow>

         {inquiries.length === 0 ? (
            <Empty>
               No credit purchases yet. Request credits from your distributor on the
               Partners page.
            </Empty>
         ) : (
            <Card>
               <Table>
                  <THead>
                     <Th>Distributor</Th>
                     <Th $right>Credits</Th>
                     <Th $right>Price/credit</Th>
                     <Th $right>Total</Th>
                     <Th>Status</Th>
                     <Th $right>When</Th>
                  </THead>
                  {inquiries.map((i) => {
                     const price =
                        i.negotiatedPricePerCredit ?? i.proposedPricePerCredit;
                     return (
                        <Tr key={i._id}>
                           <Td>{distributorName(i.distributor)}</Td>
                           <TdRight>{i.requestedCredits.toLocaleString()}</TdRight>
                           <TdRight>₱{price}</TdRight>
                           <TdRight $strong>
                              ₱{(i.requestedCredits * price).toLocaleString()}
                           </TdRight>
                           <Td>
                              <StatusPill $status={i.status}>
                                 {PURCHASE_STATUS_LABEL[i.status] ?? i.status}
                              </StatusPill>
                           </Td>
                           <TdRight>{formatRelativeDate(i.createdAt)}</TdRight>
                        </Tr>
                     );
                  })}
               </Table>
            </Card>
         )}
      </>
   );
};

/* ─── Profile ───────────────────────────────────────────────────────── */

/* ─── Requests inbox (buyers asking to buy credits from this reseller) ─── */

type InquiryBuyer = string | { name?: string; email?: string };
const buyerName = (b: InquiryBuyer): string => {
   if (b && typeof b === 'object') return b.name || b.email || 'Buyer';
   return 'Buyer';
};

const ResellerInboxPane: React.FC = () => {
   const qc = useQueryClient();
   const { data: inquiries = [], isLoading } = useQuery({
      queryKey: ['vendo', 'reseller', 'inbox'],
      queryFn: () => listResellerInboxApi(),
   });

   const refresh = () => {
      qc.invalidateQueries({ queryKey: ['vendo', 'reseller', 'inbox'] });
      qc.invalidateQueries({ queryKey: ['credits'] });
   };

   const approve = useMutation({
      mutationFn: (id: string) => approveResellerInquiryApi(id),
      onSuccess: () => {
         toast.success('Request approved — buyer can now pay you');
         refresh();
      },
      onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not approve'),
   });
   const fulfill = useMutation({
      mutationFn: (id: string) => fulfillResellerInquiryApi(id),
      onSuccess: () => {
         toast.success('Credits sent to the buyer 🎉');
         refresh();
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Could not send credits'),
   });
   const reject = useMutation({
      mutationFn: (id: string) => rejectResellerInquiryApi(id),
      onSuccess: () => {
         toast.success('Request rejected');
         refresh();
      },
      onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not reject'),
   });

   const busy = approve.isPending || fulfill.isPending || reject.isPending;

   if (isLoading) return <SkeletonLines lines={3} />;

   // Open requests (pending/approved) first, then recent closed ones.
   const open = inquiries.filter(
      (i) => i.status === 'pending' || i.status === 'approved'
   );
   const closed = inquiries.filter(
      (i) => i.status === 'fulfilled' || i.status === 'rejected'
   );

   if (inquiries.length === 0) {
      return (
         <Empty>
            No credit requests yet. Share your public link so customers can buy
            credits from you.
         </Empty>
      );
   }

   const renderRow = (inq: ApiInquiry) => (
      <Card key={inq._id}>
         <ProfileRow>
            <ProfileLbl>Buyer</ProfileLbl>
            <ProfileVal>{buyerName(inq.buyer as InquiryBuyer)}</ProfileVal>
         </ProfileRow>
         <ProfileRow>
            <ProfileLbl>Credits</ProfileLbl>
            <ProfileVal>{inq.requestedCredits.toLocaleString()}</ProfileVal>
         </ProfileRow>
         <ProfileRow>
            <ProfileLbl>Total</ProfileLbl>
            <ProfileVal>
               ₱
               {(
                  inq.totalAmount ??
                  inq.requestedCredits * inq.proposedPricePerCredit
               ).toLocaleString()}
            </ProfileVal>
         </ProfileRow>
         <ProfileRow>
            <ProfileLbl>Status</ProfileLbl>
            <ProfileVal>
               <StatusPill $status={inq.status}>{inq.status}</StatusPill>
            </ProfileVal>
         </ProfileRow>
         {inq.paymentProofUrl && (
            <ProfileRow>
               <ProfileLbl>Payment proof</ProfileLbl>
               <ProfileVal>
                  <ProfileLink
                     href={inq.paymentProofUrl}
                     target="_blank"
                     rel="noopener noreferrer"
                  >
                     View receipt ↗
                  </ProfileLink>
               </ProfileVal>
            </ProfileRow>
         )}
         {(inq.status === 'pending' || inq.status === 'approved') && (
            <InboxActions>
               {inq.status === 'pending' && (
                  <ActBtn
                     $kind="approve"
                     disabled={busy}
                     onClick={() => approve.mutate(inq._id)}
                  >
                     Approve
                  </ActBtn>
               )}
               {inq.status === 'approved' && (
                  <ActBtn
                     $kind="approve"
                     disabled={busy}
                     onClick={() => fulfill.mutate(inq._id)}
                  >
                     Send credits
                  </ActBtn>
               )}
               <ActBtn
                  $kind="reject"
                  disabled={busy}
                  onClick={() => reject.mutate(inq._id)}
               >
                  Reject
               </ActBtn>
            </InboxActions>
         )}
      </Card>
   );

   return (
      <>
         {open.map(renderRow)}
         {closed.length > 0 && <SectionLbl>Past requests</SectionLbl>}
         {closed.map(renderRow)}
      </>
   );
};

interface ResellerDraft {
   displayName: string;
   blurb: string;
   photoUrl: string;
   contactPhone: string;
   facebookUrl: string;
}

const ProfilePane: React.FC<{ reseller: ApiReseller | null }> = ({ reseller }) => {
   const qc = useQueryClient();
   const [draft, setDraft] = useState<ResellerDraft | null>(null);

   // Seed the edit form from the loaded reseller once it arrives.
   useEffect(() => {
      if (reseller) {
         setDraft({
            displayName: reseller.displayName ?? '',
            blurb: reseller.blurb ?? '',
            photoUrl: reseller.photoUrl ?? '',
            contactPhone: reseller.contactPhone ?? '',
            facebookUrl: reseller.facebookUrl ?? '',
         });
      }
   }, [reseller]);

   const save = useMutation({
      mutationFn: () =>
         updateMyResellerProfileApi({
            displayName: (draft?.displayName ?? '').trim(),
            blurb: (draft?.blurb ?? '').trim(),
            photoUrl: (draft?.photoUrl ?? '').trim(),
            contactPhone: (draft?.contactPhone ?? '').trim(),
            facebookUrl: (draft?.facebookUrl ?? '').trim(),
         }),
      onSuccess: () => {
         toast.success('Profile saved');
         qc.invalidateQueries({ queryKey: ['vendo', 'reseller', 'me'] });
      },
      onError: (e: any) =>
         toast.error(e?.response?.data?.error || 'Could not save profile'),
   });

   if (!reseller || !draft) return <Muted>No reseller profile found.</Muted>;
   const s = reseller.stats;

   const dirty =
      draft.displayName !== (reseller.displayName ?? '') ||
      draft.blurb !== (reseller.blurb ?? '') ||
      draft.photoUrl !== (reseller.photoUrl ?? '') ||
      draft.contactPhone !== (reseller.contactPhone ?? '') ||
      draft.facebookUrl !== (reseller.facebookUrl ?? '');
   const nameEmpty = !draft.displayName.trim();

   return (
      <>
         <StatsRow>
            <StatCard>
               <StatLbl>Points</StatLbl>
               <StatVal $brand>{(s?.points ?? 0).toLocaleString()}</StatVal>
            </StatCard>
            <StatCard>
               <StatLbl>Credits left</StatLbl>
               <StatVal $green>{(s?.creditsLeft ?? 0).toLocaleString()}</StatVal>
            </StatCard>
            <StatCard>
               <StatLbl>Credits sold</StatLbl>
               <StatVal>{(s?.creditsSold ?? 0).toLocaleString()}</StatVal>
            </StatCard>
            <StatCard>
               <StatLbl>Revenue</StatLbl>
               <StatVal>₱{(s?.revenue ?? 0).toLocaleString()}</StatVal>
            </StatCard>
         </StatsRow>

         <ProfileColumns>
            {/* LEFT — who you are */}
            <ProfileCol>
               <Card>
                  <EditField>
                     <ProfileLbl>Photo</ProfileLbl>
                     <FieldHint>Shown on your public link and the reseller list.</FieldHint>
                     <FileUploader
                        value={draft.photoUrl}
                        onChange={(url) =>
                           setDraft((d) => (d ? { ...d, photoUrl: url } : d))
                        }
                     />
                  </EditField>

                  <EditField>
                     <ProfileLbl>Short blurb</ProfileLbl>
                     <FieldHint>One line buyers see on your card. Optional.</FieldHint>
                     <EditTextarea
                        rows={3}
                        value={draft.blurb}
                        placeholder="e.g. Fast credit drops · message me anytime"
                        onChange={(e) =>
                           setDraft((d) => (d ? { ...d, blurb: e.target.value } : d))
                        }
                     />
                  </EditField>

                  <EditField>
                     <ProfileLbl>Contact phone</ProfileLbl>
                     <EditInput
                        value={draft.contactPhone}
                        placeholder="e.g. +63 917 555 1234"
                        onChange={(e) =>
                           setDraft((d) => (d ? { ...d, contactPhone: e.target.value } : d))
                        }
                     />
                  </EditField>

                  <ProfileRow>
                     <ProfileLbl>Status</ProfileLbl>
                     <ProfileVal>
                        <StatusPill $status={reseller.status}>
                           {reseller.status}
                        </StatusPill>
                     </ProfileVal>
                  </ProfileRow>
                  {reseller.govIdUrl && (
                     <ProfileRow>
                        <ProfileLbl>Government ID</ProfileLbl>
                        <ProfileVal>
                           <ProfileLink
                              href={reseller.govIdUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                           >
                              View ID ↗
                           </ProfileLink>
                        </ProfileVal>
                     </ProfileRow>
                  )}
                  <ProfileRow>
                     <ProfileLbl>Joined</ProfileLbl>
                     <ProfileVal>{formatRelativeDate(reseller.createdAt)}</ProfileVal>
                  </ProfileRow>
               </Card>
            </ProfileCol>

            {/* RIGHT — your shareable link + name */}
            <ProfileCol>
               <PartnerShareLink slug={reseller.slug} />
               <Card>
                  <EditField>
                     <ProfileLbl>Display name *</ProfileLbl>
                     <FieldHint>The name buyers see on your public link and card.</FieldHint>
                     <EditInput
                        value={draft.displayName}
                        onChange={(e) =>
                           setDraft((d) => (d ? { ...d, displayName: e.target.value } : d))
                        }
                     />
                     {nameEmpty && <FieldErr>Display name can't be empty.</FieldErr>}
                  </EditField>

                  <EditField>
                     <ProfileLbl>Facebook URL</ProfileLbl>
                     <FieldHint>Buyers see this so they can message you.</FieldHint>
                     <EditInput
                        value={draft.facebookUrl}
                        placeholder="https://facebook.com/your-profile"
                        onChange={(e) =>
                           setDraft((d) => (d ? { ...d, facebookUrl: e.target.value } : d))
                        }
                     />
                  </EditField>
               </Card>
            </ProfileCol>
         </ProfileColumns>

         <SaveBar>
            <SaveHint>
               {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
            </SaveHint>
            <SaveBtn
               type="button"
               disabled={!dirty || nameEmpty || save.isPending}
               onClick={() => save.mutate()}
            >
               {save.isPending ? 'Saving…' : 'Save profile'}
            </SaveBtn>
         </SaveBar>
      </>
   );
};

export default Reseller;

/* ─── styles ─────────────────────────────────────────────────────────── */

const Wrapper = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
`;

const Muted = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
   padding: 16px 0;
`;

const Empty = styled.div`
   padding: 28px 16px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   background: ${colors.WHITE};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;

const TabBar = styled.div`
   display: flex;
   gap: 6px;
   border-bottom: 1px solid ${colors.GRAY13};
   overflow-x: auto;
   flex-wrap: nowrap;
   -webkit-overflow-scrolling: touch;
   scrollbar-width: none;
   &::-webkit-scrollbar { display: none; }
`;

const TabBtn = styled.button<{ $active: boolean }>`
   flex-shrink: 0;
   white-space: nowrap;
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 16px;
   border: none;
   background: transparent;
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid
      ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   margin-bottom: -1px;
`;

const Card = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   overflow: hidden;
`;

const Table = styled.div`
   display: flex;
   flex-direction: column;
`;

const THead = styled.div`
   display: grid;
   grid-template-columns: 1.4fr 1.4fr 0.8fr 1.2fr 0.9fr;
   gap: 8px;
   padding: 10px 14px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
`;

const Th = styled.div<{ $right?: boolean }>`
   font-size: 11px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
`;

const Tr = styled.div`
   display: grid;
   grid-template-columns: 1.4fr 1.4fr 0.8fr 1.2fr 0.9fr;
   gap: 8px;
   padding: 12px 14px;
   align-items: center;
   border-bottom: 1px solid ${colors.GRAY14};
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   &:last-child {
      border-bottom: none;
   }
`;

const Td = styled.div`
   word-break: break-word;
`;

const TdRight = styled.div<{ $strong?: boolean; $color?: string }>`
   text-align: right;
   font-weight: ${({ $strong }) => ($strong ? 800 : 500)};
   color: ${({ $color }) => $color ?? colors.MAIN_DARK};
`;

const DirPill = styled.span<{ $out: boolean }>`
   font-size: 11px;
   font-weight: 700;
   padding: 3px 9px;
   border-radius: 999px;
   color: ${({ $out }) => ($out ? colors.DANGER : colors.SUCCESS)};
   background: ${({ $out }) =>
      $out ? `${colors.DANGER}14` : `${colors.SUCCESS}14`};
`;

const StatusPill = styled.span<{ $status: string }>`
   font-size: 11px;
   font-weight: 700;
   text-transform: capitalize;
   padding: 4px 10px;
   border-radius: 999px;
   color: ${({ $status }) =>
      $status === 'fulfilled' || $status === 'approved'
         ? colors.SUCCESS
         : $status === 'rejected'
         ? colors.DANGER
         : colors.GRAY5};
   background: ${colors.GRAY14};
`;

const StatsRow = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
   gap: 12px;
`;

const StatCard = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   padding: 14px 16px;
`;

const StatLbl = styled.p`
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
`;

const StatVal = styled.p<{ $green?: boolean; $brand?: boolean }>`
   font-size: 24px;
   font-weight: 900;
   margin-top: 4px;
   color: ${({ $green, $brand }) =>
      $green ? colors.SUCCESS : $brand ? colors.PRIMARY : colors.MAIN_DARK};
`;

const StatSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const ProfileRow = styled.div`
   display: grid;
   grid-template-columns: 140px 1fr;
   gap: 12px;
   padding: 12px 16px;
   border-bottom: 1px solid ${colors.GRAY14};
   &:last-child {
      border-bottom: none;
   }
`;

const ProfileLbl = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY5};
`;

const ProfileVal = styled.div`
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   word-break: break-word;
`;

const ProfileLink = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 600;
   text-decoration: none;
   &:hover {
      text-decoration: underline;
   }
`;

const Banner = styled.div`
   display: flex;
   gap: 14px;
   padding: 18px;
   background: var(--app-brand-tint);
   border-radius: 14px;
`;

const BannerIcon = styled.span`
   font-size: 28px;
`;

const BannerBody = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const BannerTitle = styled.p`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const BannerText = styled.p`
   font-size: 13px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const BannerBtn = styled.button`
   align-self: flex-start;
   margin-top: 4px;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   padding: 8px 16px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   cursor: pointer;
`;

/* ─── editable profile fields ────────────────────────────────────────── */

const EditField = styled.div`
   padding: 14px 16px;
   border-bottom: 1px solid ${colors.GRAY14};
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const FieldHint = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin: 0;
`;

const FieldErr = styled.p`
   font-size: 12px;
   color: ${colors.DANGER};
   margin: 0;
`;

const EditInput = styled.input`
   width: 100%;
   padding: 10px 12px;
   font-size: 14px;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const EditTextarea = styled.textarea`
   width: 100%;
   padding: 10px 12px;
   font-size: 14px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   resize: vertical;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const SaveRow = styled.div`
   display: flex;
   justify-content: flex-end;
   padding: 14px 16px;
   border-bottom: 1px solid ${colors.GRAY14};
`;

const SaveBtn = styled.button`
   padding: 10px 20px;
   font-size: 14px;
   font-weight: 700;
   color: ${colors.WHITE};
   background: ${colors.PRIMARY};
   border: none;
   border-radius: 10px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

/* ─── inbox actions ──────────────────────────────────────────────────── */

const SectionLbl = styled.p`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin: 20px 0 4px;
`;

const InboxActions = styled.div`
   display: flex;
   gap: 8px;
   padding: 14px 16px;
`;

const ActBtn = styled.button<{ $kind: 'approve' | 'reject' }>`
   flex: 1;
   padding: 10px 14px;
   font-size: 13px;
   font-weight: 700;
   border-radius: 10px;
   border: none;
   cursor: pointer;
   color: ${({ $kind }) => ($kind === 'approve' ? colors.WHITE : colors.DANGER)};
   background: ${({ $kind }) =>
      $kind === 'approve' ? colors.PRIMARY : 'transparent'};
   ${({ $kind }) =>
      $kind === 'reject' ? `border: 1px solid ${colors.GRAY12};` : ''}
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

/* ─── 2-column profile layout ────────────────────────────────────────── */

const ProfileColumns = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   align-items: start;
   @media (max-width: 820px) {
      grid-template-columns: 1fr;
   }
`;

const ProfileCol = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
   min-width: 0;
`;

const SaveBar = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 14px;
   margin-top: 16px;
`;

const SaveHint = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
